import {
  HttpErrorResponse,
  HttpEvent,
  HttpEventType,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { FedevAuthService } from '@bmw-fedev/auth';
import { OAuthService } from 'angular-oauth2-oidc';
import { DsToastService } from '@bmw-ds/components';
import { TranslateService } from '@ngx-translate/core';
import { DateTime } from 'luxon';
import { ToastExtendedService } from './toast-extended.service';
import {
  extractApiError,
  getDeepestMessage,
  isLikelyStackTrace,
} from './http/api-error.utils';
import {
  SKIP_GLOBAL_ERROR_TOAST,
  SKIP_GLOBAL_ERROR_TOAST_STATUSES,
} from './http/skip-global-error.token';
import {
  buildHttpErrorReport,
  buildReportContext,
  HttpErrorRequestInfo,
} from './http/error-report';
import { classifyHttpError } from './http/http-error-classifier';
import { markRecoverySuccess, recoverSession } from './session-recovery';

const TOAST_DEBOUNCE_MS = 1000;
const GENERIC_4XX_KEY = 'api.errors.generic4xx';
const NETWORK_KEY = 'api.errors.network';
const GENERIC_NETWORK_KEY = 'api.errors.genericNetwork';

const lastToastAt = new Map<string, number>();

const shouldToast = (id: string): boolean => {
  const now = Date.now();
  if (now - (lastToastAt.get(id) ?? 0) < TOAST_DEBOUNCE_MS) {
    return false;
  }
  lastToastAt.set(id, now);
  return true;
};

const backendMessage = (error: HttpErrorResponse): string | null => {
  const apiError = extractApiError(error);
  if (!apiError) {
    return null;
  }
  const deepest = getDeepestMessage(apiError);
  return deepest && !isLikelyStackTrace(deepest) ? deepest : null;
};

const resolveTranslation = (
  translate: TranslateService,
  key: string,
  fallbackKey: string
): string => {
  const translated = translate.instant(key);
  return translated === key ? translate.instant(fallbackKey) : translated;
};

const CUSTOM_BAD_REQUEST_TYPE = 'CustomBadRequestException';

const customBadRequestMessage = (
  error: HttpErrorResponse,
  translate: TranslateService
): string | null => {
  const apiError = extractApiError(error);
  if (apiError?.type !== CUSTOM_BAD_REQUEST_TYPE) {
    return null;
  }
  const messages: string[] = [];
  for (const violation of apiError.violations ?? []) {
    const { namespace, key, parameters } = violation.details ?? {};
    if (!namespace || !key) {
      continue;
    }
    const translationKey = `${namespace}.${key}`;
    const translated = translate.instant(translationKey, parameters);
    messages.push(
      translated === translationKey ? violation.message : translated
    );
  }
  return messages.length > 0 ? messages.join('\n') : null;
};

const toast4xx = (
  error: HttpErrorResponse,
  translate: TranslateService,
  toast: DsToastService
): void => {
  const id = `warn-${error.status}`;
  if (!shouldToast(id)) {
    return;
  }
  const toastText =
    customBadRequestMessage(error, translate) ??
    backendMessage(error) ??
    resolveTranslation(
      translate,
      `api.errors.${error.status}`,
      GENERIC_4XX_KEY
    );
  toast.pushToast({ id, tone: 'caution', toastText });
};

const toastNetwork = (
  translate: TranslateService,
  toast: DsToastService
): void => {
  const id = 'network-error';
  if (!shouldToast(id)) {
    return;
  }
  toast.pushToast({
    id,
    tone: 'critical',
    toastText: resolveTranslation(translate, NETWORK_KEY, GENERIC_NETWORK_KEY),
  });
};

const toast5xx = (
  error: HttpErrorResponse,
  request: HttpErrorRequestInfo,
  auth: FedevAuthService,
  toastExt: ToastExtendedService
): void => {
  const id = `err-${error.status}`;
  if (!shouldToast(id)) {
    return;
  }
  const context = buildReportContext(
    auth.currentUser() ?? 'unknown',
    DateTime.now().toISO() ?? ''
  );
  toastExt.pushErrorToast({
    id,
    tone: 'critical',
    toastText: buildHttpErrorReport(error, request, context),
  });
};

export const errorInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const auth = inject(FedevAuthService);
  const oauth = inject(OAuthService);
  const router = inject(Router);
  const toast = inject(DsToastService);
  const toastExt = inject(ToastExtendedService);
  const translate = inject(TranslateService);

  return next(req).pipe(
    tap((event) => {
      if (event.type === HttpEventType.Response) {
        markRecoverySuccess();
      }
    }),
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse)) {
        return throwError(() => error);
      }
      const outcome = classifyHttpError(error, oauth.hasValidAccessToken());
      const skipToast =
        req.context.get(SKIP_GLOBAL_ERROR_TOAST) ||
        req.context
          .get(SKIP_GLOBAL_ERROR_TOAST_STATUSES)
          .includes(error.status);
      switch (outcome) {
        case 'recover':
          recoverSession(auth, router, router.url);
          break;
        case 'toast-4xx':
          if (!skipToast) {
            toast4xx(error, translate, toast);
          }
          break;
        case 'toast-network':
          if (!skipToast) {
            toastNetwork(translate, toast);
          }
          break;
        case 'toast-5xx':
          if (!skipToast) {
            toast5xx(
              error,
              { method: req.method, url: req.urlWithParams },
              auth,
              toastExt
            );
          }
          break;
        case 'silent':
          break;
      }
      return throwError(() => error);
    })
  );
};
